import { useState } from "react";
import {
    Database, BookOpen, GraduationCap, School, Globe2, MapPin, Building2,
    Mailbox, Languages, Award, FileText, Layers, Trash2, FileClock,
    LayoutDashboard, ShieldAlert, Save,
} from "lucide-react";

import { useSuperMode } from "../../context/SuperModeContext";
import MasterDataDashboard from "./master-data/MasterDataDashboard";
import KindEditor from "./master-data/KindEditor";
import AuditLogsPanel from "./master-data/AuditLogsPanel";
import RecycleBinPanel from "./master-data/RecycleBinPanel";
import BackupRestorePanel from "./master-data/BackupRestorePanel";
import GlobalSearch from "./master-data/GlobalSearch";

/**
 * V53 — Master Data Management hub (production).
 *
 * Sub-tabs:
 *  Dashboard | Subjects | Grades | Boards | Countries | States | Cities | Pincodes
 *  | Languages | Exam Cats | Curriculum Types | Curriculum Packs | Audit | Recycle Bin | Backup
 *
 * All write operations are gated server-side by the Super Mode middleware
 * (/api/admin/master* requires X-Super-Mode-Token). The UI also surfaces a
 * banner when not in Super Mode so admins understand why writes will fail.
 */

const TABS = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "subject", label: "Subjects", icon: BookOpen },
    { id: "grade", label: "Grades", icon: GraduationCap },
    { id: "board", label: "Boards", icon: School },
    { id: "country", label: "Countries", icon: Globe2 },
    { id: "state", label: "States", icon: MapPin },
    { id: "city", label: "Cities", icon: Building2 },
    { id: "pincode", label: "Pincodes", icon: Mailbox },
    { id: "language", label: "Languages", icon: Languages },
    { id: "exam_category", label: "Exam Categories", icon: Award },
    { id: "curriculum_type", label: "Curriculum Types", icon: FileText },
    { id: "curriculum_pack", label: "Curriculum Packs", icon: Layers },
    { id: "audit", label: "Audit Logs", icon: FileClock },
    { id: "recycle", label: "Recycle Bin", icon: Trash2 },
    { id: "backup", label: "Backup", icon: Save },
];

export default function MasterDataPanel() {
    const [tab, setTab] = useState("dashboard");
    const sm = useSuperMode();
    const active = TABS.find((t) => t.id === tab);

    return (
        <div className="space-y-4" data-testid="master-data-panel">
            <header className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A] flex items-center gap-2">
                        <Database className="w-6 h-6 text-violet-700" /> Master Data Management
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold uppercase tracking-wider">V53</span>
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Single source-of-truth for every dropdown — Countries, Curriculums, Boards,
                        Grades, Subjects + mappings. CRUD, bulk import/export, draft mode, audit
                        history, soft-delete recycle bin and full backup/restore.
                    </p>
                </div>
                <div className="w-full lg:w-80 shrink-0">
                    <GlobalSearch onJump={setTab} />
                </div>
            </header>

            {/* Super mode banner */}
            {sm.isSuperAdmin && !sm.active && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm flex items-start gap-3" data-testid="md-super-mode-warning">
                    <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-bold text-amber-900">Read-only — Super Mode required for writes</div>
                        <div className="text-xs text-amber-800 mt-0.5">
                            All Add/Edit/Delete/Import/Restore actions are protected by your 6-digit MPIN.
                            Click <strong>Switch to Super Mode</strong> in the top bar to unlock.
                        </div>
                    </div>
                </div>
            )}

            {/* Sub-tabs */}
            <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-2 -mx-1 px-1 overflow-x-auto" data-testid="md-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const on = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`md-tab-${t.id}`}
                            data-on={on}
                            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition whitespace-nowrap ${
                                on
                                    ? "bg-violet-700 border-violet-700 text-white shadow-sm"
                                    : "bg-white border-slate-200 text-slate-700 hover:border-violet-400"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            <div className="min-h-[300px]">
                {tab === "dashboard" && <MasterDataDashboard onJump={setTab} />}
                {tab === "audit" && <AuditLogsPanel />}
                {tab === "recycle" && <RecycleBinPanel />}
                {tab === "backup" && <BackupRestorePanel />}
                {!["dashboard", "audit", "recycle", "backup"].includes(tab) && (
                    <KindEditor key={tab} kind={tab} label={active?.label || tab} />
                )}
            </div>
        </div>
    );
}
